/**
 * Theme Loader
 * 
 * This script loads the theme settings from the API and 
 * applies initial CSS variables before the app fully renders.
 * This prevents a flash of unstyled content or wrong theme.
 */

import themes from './index';
import { loadThemeFont } from './fontLoader';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5001';

// Default theme
const DEFAULT_THEME = 'situation-room-default';

// Helper function to get theme from URL parameters
const getThemeFromUrl = () => {
  if (typeof window !== 'undefined') {
    const urlParams = new URLSearchParams(window.location.search);
    const themeParam = urlParams.get('theme');
    // Check if the theme from URL exists in our available themes
    if (themeParam && themes[themeParam]) {
      return themeParam;
    }
  }
  return null;
};

// Function to fetch the theme setting from the API
async function fetchThemeSetting() {
  try {
    // First check URL parameters
    const urlTheme = getThemeFromUrl();
    if (urlTheme) {
      return urlTheme;
    }
    
    // If no URL theme, fetch from API
    const response = await fetch(`${API_BASE_URL}/api/settings/theme`);
    if (!response.ok) throw new Error('Failed to fetch theme setting');
    const data = await response.json();
    return data.value || DEFAULT_THEME;
  } catch (error) {
    console.error('Error loading theme setting:', error);
    return DEFAULT_THEME;
  }
}

// Function to apply CSS variables based on theme
export function applyCssVariables(themeName) {
  const theme = themes[themeName] || themes[DEFAULT_THEME];
  if (!theme || !theme.palette) {
    console.warn(`Theme ${themeName} not found or invalid`);
    return;
  }

  const root = document.documentElement;
  
  // Primary color
  root.style.setProperty('--color-primary', theme.palette.primary.main);
  root.style.setProperty('--color-secondary', theme.palette.secondary.main);
  root.style.setProperty('--color-error', theme.palette.error.main);
  root.style.setProperty('--color-warning', theme.palette.warning.main);
  root.style.setProperty('--color-info', theme.palette.info.main);
  root.style.setProperty('--color-success', theme.palette.success.main);
  root.style.setProperty('--color-background', theme.palette.background.default);
  root.style.setProperty('--color-background-paper', theme.palette.background.paper);
  root.style.setProperty('--color-text-primary', theme.palette.text.primary);
  root.style.setProperty('--color-text-secondary', theme.palette.text.secondary);
  root.style.setProperty('--color-text-disabled', theme.palette.text.disabled);
  
  // Helper function to extract RGB values from hex color
  const hexToRgb = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : null;
  };

  // Create opacity variations for primary and secondary colors
  const primaryRgb = hexToRgb(theme.palette.primary.main);
  const secondaryRgb = hexToRgb(theme.palette.secondary.main);
  
  if (primaryRgb) {
    root.style.setProperty('--color-primary-01', `rgba(${primaryRgb.r}, ${primaryRgb.g}, ${primaryRgb.b}, 0.01)`);
    root.style.setProperty('--color-primary-02', `rgba(${primaryRgb.r}, ${primaryRgb.g}, ${primaryRgb.b}, 0.02)`);
    root.style.setProperty('--color-primary-03', `rgba(${primaryRgb.r}, ${primaryRgb.g}, ${primaryRgb.b}, 0.03)`);
    root.style.setProperty('--color-primary-05', `rgba(${primaryRgb.r}, ${primaryRgb.g}, ${primaryRgb.b}, 0.05)`);
    root.style.setProperty('--color-primary-06', `rgba(${primaryRgb.r}, ${primaryRgb.g}, ${primaryRgb.b}, 0.06)`);
    root.style.setProperty('--color-primary-08', `rgba(${primaryRgb.r}, ${primaryRgb.g}, ${primaryRgb.b}, 0.08)`);
    root.style.setProperty('--color-primary-10', `rgba(${primaryRgb.r}, ${primaryRgb.g}, ${primaryRgb.b}, 0.1)`);
    root.style.setProperty('--color-primary-12', `rgba(${primaryRgb.r}, ${primaryRgb.g}, ${primaryRgb.b}, 0.12)`);
    root.style.setProperty('--color-primary-15', `rgba(${primaryRgb.r}, ${primaryRgb.g}, ${primaryRgb.b}, 0.15)`);
    root.style.setProperty('--color-primary-20', `rgba(${primaryRgb.r}, ${primaryRgb.g}, ${primaryRgb.b}, 0.2)`);
    root.style.setProperty('--color-primary-80', `rgba(${primaryRgb.r}, ${primaryRgb.g}, ${primaryRgb.b}, 0.8)`);
  }
  
  if (secondaryRgb) {
    root.style.setProperty('--color-secondary-03', `rgba(${secondaryRgb.r}, ${secondaryRgb.g}, ${secondaryRgb.b}, 0.03)`);
    root.style.setProperty('--color-secondary-05', `rgba(${secondaryRgb.r}, ${secondaryRgb.g}, ${secondaryRgb.b}, 0.05)`);
  }

  // Also update font-family
  root.style.setProperty('--font-family-base', theme.typography.fontFamily);
  root.style.setProperty('--font-family-heading', theme.typography.fontFamily);
  
  // Applied CSS variables for theme
}

// Initialize theme by setting data-theme attribute and CSS variables
export async function initializeTheme() {
  const themeName = await fetchThemeSetting();
  document.documentElement.setAttribute('data-theme', themeName);
  applyCssVariables(themeName);
  
  // Load the appropriate font
  loadThemeFont(themeName);
  
  return themeName;
}

// Execute on module load
if (typeof window !== 'undefined') {
  initializeTheme().catch(err => {
    console.error('Failed to initialize theme:', err);
    // Fallback to default theme if initialization fails
    document.documentElement.setAttribute('data-theme', DEFAULT_THEME);
    applyCssVariables(DEFAULT_THEME);
    loadThemeFont(DEFAULT_THEME);
  });
}

export default { initializeTheme, applyCssVariables }; 